import {
  diariosPendentes,
  marcarSincronizado,
  registrarFalha,
  type DiarioLocal,
} from './offline'
import { supabase } from './supabase'
import { mensagemDeErro } from './utilitarios'

/**
 * Sincronização da fila offline.
 *
 * Chamada em três momentos: ao salvar no diário, ao abrir o app e
 * quando o navegador avisa que a conexão voltou. No Android o
 * Background Sync cobriria isso sozinho, mas o Safari não o implementa
 * — por isso a reconciliação na abertura é o caminho principal, não uma
 * rede de segurança.
 */

let sincronizando = false

export interface ResultadoSincronizacao {
  enviados: number
  pendentes: number
}

export async function sincronizarDiarios(): Promise<ResultadoSincronizacao> {
  if (sincronizando) return { enviados: 0, pendentes: 0 }
  if (!navigator.onLine) {
    const fila = await diariosPendentes()
    return { enviados: 0, pendentes: fila.length }
  }

  sincronizando = true
  let enviados = 0

  try {
    const fila = await diariosPendentes()

    for (const item of fila) {
      try {
        await enviarDiario(item)
        await marcarSincronizado(item.chave)
        enviados += 1
      } catch (erro) {
        await registrarFalha(item.chave, mensagemDeErro(erro))
      }
    }

    const restantes = await diariosPendentes()
    return { enviados, pendentes: restantes.length }
  } finally {
    sincronizando = false
  }
}

async function enviarDiario(item: DiarioLocal) {
  const { praticas, ...campos } = item.entrada

  // O UNIQUE (matricula_id, data) faz o upsert ser idempotente: reenviar
  // o mesmo dia atualiza a linha em vez de duplicar.
  const { data, error } = await supabase
    .from('diarios')
    .upsert(
      {
        matricula_id: item.matriculaId,
        data: item.data,
        dia_numero: item.diaNumero,
        ...campos,
      },
      { onConflict: 'matricula_id,data' },
    )
    .select('id')
    .single()

  if (error) throw error

  const diarioId = (data as { id: string }).id

  // Substitui o checklist inteiro: é mais simples e mais seguro do que
  // reconciliar item a item, e o volume é de sete linhas por dia.
  const { error: erroLimpeza } = await supabase
    .from('diario_praticas')
    .delete()
    .eq('diario_id', diarioId)
  if (erroLimpeza) throw erroLimpeza

  if (praticas.length > 0) {
    const { error: erroPraticas } = await supabase.from('diario_praticas').insert(
      praticas.map((praticaId) => ({
        diario_id: diarioId,
        pratica_id: praticaId,
        concluida: true,
      })),
    )
    if (erroPraticas) throw erroPraticas
  }
}

/** Liga a sincronização à volta da conexão e à reabertura do app. */
export function observarConexao() {
  const aoVoltar = () => void sincronizarDiarios()

  window.addEventListener('online', aoVoltar)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') aoVoltar()
  })

  return () => window.removeEventListener('online', aoVoltar)
}
